import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { SeIcon } from "@se/web-ui-react";
import "./MGTariff.scss";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import { useDualTranslation } from "../utils/i18nHelpers";
import TimeSlotsDisplay from "./TimeSlotDisplay";
import WeekdaysSelector from "./WeekDaySelector";
import DynamicTabbar from "./TabBar";
import {
  generateTimeOptions,
  formatTime,
  getUsedWeekdays,
  WEEK_DAYS,
  getRandomInt,
  DEMAND_NOTICE,
} from "./TarifUtils";
import TariffDataSheet from "./TariffDataSheet";
import { MonthPickerInput } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import { Button, useMantineColorScheme, Group, Select } from "@mantine/core";
import PropTypes from "prop-types";
import DemandNoticeSelector from "./DemandNoticeSelector";
import SeasonSunburstChart from "./SeasonSunburstChart";
import { Checkbox, TextInput } from "@mantine/core";
import SEConfirmModal from "../components/common/SEConfirmModal";
const DECIMAL_INPUT_REGEX = /^\d{0,12}(\.\d{0,2})?$/; // precompiled, length-limited to avoid regex hotspot
const now = new Date();
const prevYear = now.getFullYear() - 1;
const pad = (n) => n.toString().padStart(2, "0");
const formatDate = (date) => `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()}`;
const parseDdMmYyyy = (dateStr) => {
  const [day, month, year] = (dateStr || "").split("-");
  if (!day || !month || !year) return null;
  return new Date(`${year}-${month}-${day}`);
};
const randomStartDate = new Date(prevYear, 0, 1);
// imports are at the top of the file


// Helper to create a tier object with digit keys 1-9
const createTierBoundary = () => {
  const tier = { tierId: getRandomInt(1, 9999) };
  tier[1] = { name: DEMAND_NOTICE[1]?.name, value: "" };
  return tier;
};

const seasonData = [
  {
    seasonId: getRandomInt(1, 9999),
    name: "Season 1",
    startDay: `01-01-${prevYear}`,
    endDay: `31-12-${prevYear}`,
    seasonStartTime: { hour: 0, minute: 0 },
    seasonEndTime: { hour: 24, minute: 0 },
    rateMatrix: [],
    holidays: false,
    periods: [
      {
        demandTypeId: getRandomInt(1, 9999),
        demandTypeName: "Demand Type 1",
        weekDays: [1, 2, 3, 4, 5],
        rate: [
          {
            tariffId: getRandomInt(1, 9999),
            name: "PERIOD 1",
            demandType: "MonthlyMax_US",
            demandCost: "",
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [
              { startTime: { hour: 0, minute: 0 }, endTime: { hour: 24, minute: 0 } },
            ],
            options: { tierType: "Off", tierBoundaries: [createTierBoundary()] },
          },
        ],
      },
      {
        demandTypeId: getRandomInt(1, 9999),
        demandTypeName: "Demand Type 2",
        weekDays: [6, 7],
        rate: [
          {
            tariffId: getRandomInt(1, 9999),
            name: "PERIOD 1",
            demandType: "MonthlyMax_US",
            demandCost: "",
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [
              { startTime: { hour: 0, minute: 0 }, endTime: { hour: 24, minute: 0 } },
            ],
            options: { tierType: "Off", tierBoundaries: [createTierBoundary()] },
          },
        ],
      },
    ],
  },
];



export const DCTariff = forwardRef((props, ref) => {
  const { t } = useDualTranslation();
  const isReadOnly = props.isVersionCompleted;
      // Generate a unique id for this component instance for snackbar
      // Generate a robust unique id for snackbar without using Math.random (use crypto when available)
      const uniqueId = (() => {
        const prefix = 'snackbar-custom-demand-charge-';
        if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
          const arr = new Uint32Array(3);
          window.crypto.getRandomValues(arr);
          return prefix + arr.map(n => n.toString(36)).join('').slice(0, 12);
        }
        // Fallback: timestamp + counter
        const ts = Date.now().toString(36);
        if (!window.__snackCounter) window.__snackCounter = 0;
        const cnt = (window.__snackCounter++).toString(36);
        return prefix + ts + cnt;
      })();
    // Helper: true if in season 1 / demand type 1

  // NC cost state

  const [ seasonsList, setSeasonsList] = useState(
    props.tariffTemplate || seasonData
  );
  const [activeSeason, setActiveSeason] = useState(0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newItemValue, setNewItemValue] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedSeason, setSelectedSeason] = useState(
    props?.tariffTemplate?.[0] || seasonData[0]
  );
  const [calculatedEndDate, setCalculatedEndDate] = useState(
    moment(randomStartDate).add(1, "years").subtract(1, "days").toDate()
  );
  const [snackbarProps, setSnackbarProps] = useState({
    open: false,
    type: "success",
    canClose: true,
    message: t("demandCharge.snackbar.success"),
    icon: "information_circle",
    button: t("common.close"),
    duration: 5000,
  });

  const [activeDemandType, setActiveDemandType] = useState(0);
  const [editingDemandTypeIndex, setEditingDemandTypeIndex] = useState(null);
  const [newDemandTypeValue, setNewDemandTypeValue] = useState("");

  // Add state for selected demand notice options
  const [selectedDemandNoticeOptions, setSelectedDemandNoticeOptions] = useState([]);

  // Confirmation modal states
  const [showConfirmRemoveSeason, setShowConfirmRemoveSeason] = useState(false);
  const [seasonToRemove, setSeasonToRemove] = useState(null);
  const [showConfirmRemoveDemandType, setShowConfirmRemoveDemandType] = useState(false);
  const [demandTypeToRemove, setDemandTypeToRemove] = useState(null);

  // --- Copy Season Feature State ---
  const [copyFromEnabled, setCopyFromEnabled] = useState(false);
  const [copyFromSeasonId, setCopyFromSeasonId] = useState(null);
  const [showCopyConfirmModal, setShowCopyConfirmModal] = useState(false);
  const [pendingCopySeasonId, setPendingCopySeasonId] = useState(null);

  const copyRatesFromSeason = (sourceSeasonId) => {
    if (!sourceSeasonId || !selectedSeason) return;
    const sourceSeason = seasonsList.find((s) => String(s.seasonId) === String(sourceSeasonId));
    if (!sourceSeason) return;
    const updatedPeriods = sourceSeason.periods.map((sourceDg) => ({
      ...JSON.parse(JSON.stringify(sourceDg)),
      demandTypeId: getRandomInt(1, 9999),
      rate: sourceDg.rate.map((period) => ({
        ...JSON.parse(JSON.stringify(period)),
        tariffId: getRandomInt(1, 9999),
        options: {
          ...period.options,
          tierBoundaries: (period.options?.tierBoundaries || []).map((tier) => ({
            ...tier,
            tierId: getRandomInt(1, 9999),
          })),
        },
      })),
    }));
    const updatedSeason = { ...selectedSeason, periods: updatedPeriods };
    setSelectedSeason(updatedSeason);
    setActiveDemandType(0);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
    props?.onChange?.();
    callSnackbar("success", t("demandCharge.snackbar.copySeasonSuccess"));
  };

  const copyRatesFromImportTariffSeason = (sourceImportSeason) => {
    if (!sourceImportSeason || !selectedSeason) return;

    // Preserve current master demand notice key structure with empty values
    const masterTier = seasonsList[0]?.periods?.[0]?.rate?.[0]?.options?.tierBoundaries?.[0];
    const masterKeys = masterTier ? Object.keys(masterTier).filter((k) => k !== "tierId") : [];
    const masterTierCount = seasonsList[0]?.periods?.[0]?.rate?.[0]?.options?.tierBoundaries?.length || 1;

    const createEmptyTierBoundaries = () =>
      Array.from({ length: masterTierCount }, () => {
        const tier = { tierId: getRandomInt(1, 9999) };
        masterKeys.forEach((k) => {
          tier[k] = { name: DEMAND_NOTICE[k]?.name, value: k === "7" ? (DEMAND_NOTICE[7]?.value || 100) : "" };
        });
        return tier;
      });

    const updatedPeriods = (sourceImportSeason.periods || []).map((dayGroup) => ({
      demandTypeId: getRandomInt(1, 9999),
      demandTypeName: dayGroup.dayGroupName || dayGroup.demandTypeName || "Demand Type",
      weekDays: dayGroup.weekDays ? [...dayGroup.weekDays] : [],
      rate: (dayGroup.rate || []).map((period) => ({
        tariffId: getRandomInt(1, 9999),
        name: period.name,
        demandType: "MonthlyMax_US",
        demandCost: "",
        minPowerValue: 0,
        maxPowerValue: "Infinity",
        timeSlots: period.timeSlots ? JSON.parse(JSON.stringify(period.timeSlots)) : [],
        options: {
          tierType: "Off",
          tierBoundaries: createEmptyTierBoundaries(),
        },
      })),
    }));

    const updatedSeason = {
      ...selectedSeason,
      periods: updatedPeriods,
    };
    setSelectedSeason(updatedSeason);
    setActiveDemandType(0);
    setSeasonsList((sl) => sl.map((s) => (s.seasonId === updatedSeason.seasonId ? updatedSeason : s)));
    setCurrentDate(new Date());
    props?.onChange?.();
    callSnackbar("success", t("demandCharge.snackbar.copySeasonSuccess"));
  };

  // Show copy UI if more than one DC season exists OR import tariff seasons are available
  const showCopyUI = (seasonsList.length > 1 || ((props.getImportTariffSeasons?.() || props.importTariffSeasons)?.length > 0)) && !isReadOnly;

  const [isNcCost, setIsNcCost] = useState(!!selectedSeason.isNcCost);
const [ncCost, setNcCost] = useState(selectedSeason.ncCost || "");
    const isMasterContext =( activeSeason === 0 && activeDemandType === 0);
  // Update selectedSeason.nccost when ncCostValue changes
useEffect(() => {
  setSelectedSeason((prev) => ({ ...prev, isNcCost, ncCost: isNcCost ? ncCost : undefined }));
}, [isNcCost, ncCost]);
  useEffect(() => {
    if (props.tariffTemplate === null) {
      setSeasonsList(seasonData);
      setActiveSeason(0);
      setSelectedSeason(seasonData[0]);
      setIsNcCost(!!seasonData?.[0]?.isNcCost);
      setNcCost(seasonData?.[0]?.ncCost || "");
      return;
    }

    if (props.tariffTemplate) {
      setSeasonsList(props.tariffTemplate);
      setActiveSeason(0);
      setSelectedSeason(props?.tariffTemplate?.[0]);
      setIsNcCost(!!props?.tariffTemplate?.[0]?.isNcCost);
      setNcCost(props?.tariffTemplate?.[0]?.ncCost || "");
    }
  }, [props.tariffTemplate]);

  const timeOptions = generateTimeOptions();
  useImperativeHandle(ref, () => ({
    getJsonData: () => {
      return seasonsList;
    },
  }));
  const isFirstSeason = useCallback(() => {
    return seasonsList.length > 0 && seasonsList[0].seasonId === selectedSeason.seasonId;
  }, [selectedSeason, seasonsList]);

  const callSnackbar = (type, message) => {
    setSnackbarProps({
      type,
      message,
    });
    const el = document.getElementById(uniqueId);
    if (el) el.open = true;
  };

  const addSeason = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Check previous season's start and end day
    if (seasonsList.length > 0) {
      const prevSeason = seasonsList[seasonsList.length - 1];
      if (prevSeason.startDay === prevSeason.endDay) {
        callSnackbar(
          "error",
          t("demandCharge.errors.prevSeasonDifferent")
        );
        return;
      }
    }

    if (
      !isFirstSeason() &&
      moment(calculatedEndDate).isSame(moment(selectedSeason.startDay))
    ) {
      callSnackbar("error", t("demandCharge.errors.sameStartDate"));
      return;
    }
    const firstSeasonStart = parseDdMmYyyy(seasonsList?.[0]?.startDay);
    const firstSeasonYear = firstSeasonStart ? firstSeasonStart.getFullYear() : prevYear;
    const yearEnd = new Date(firstSeasonYear, 11, 31);

    const lastSeason = seasonsList[seasonsList.length - 1];
    const lastSeasonStart = parseDdMmYyyy(lastSeason?.startDay);
    const nextSeasonStart = moment(lastSeasonStart)
      .add(1, "months")
      .startOf("month")
      .toDate();

    if (moment(nextSeasonStart).isAfter(yearEnd)) {
      callSnackbar("error", t("demandCharge.errors.sameStartDate"));
      return;
    }

    const newSeasonStartDate = formatDate(nextSeasonStart);
    
    // Validate new season falls within 1-year window from Season 1's start date
    if (seasonsList.length > 0) {
      const [nsd, nsm, nsy] = newSeasonStartDate.split('-');
      const newStartDate = new Date(`${nsy}-${nsm}-${nsd}`);
      
      const windowEnd = yearEnd;
      
      if (moment(newStartDate).isAfter(windowEnd)) {
        callSnackbar("error",  t("demandCharge.errors.newSeasonOutsideWindow"));
        return;
      }
    }
    
    const newSeasonEndDate = formatDate(yearEnd);

    const nextSeasonNumber = seasonsList.length + 1;

    // Helper function to clone a period with new IDs
    const clonePeriodWithNewIds = (period) => {
      return {
        ...period,
        tariffId: getRandomInt(1, 9999),
        options: {
          ...period.options,
          tierBoundaries: period.options?.tierBoundaries?.map(tier => {
            const newTier = { tierId: getRandomInt(1, 9999) };
            Object.keys(tier).forEach(key => {
              if (key !== "tierId") {
                newTier[key] = tier[key]; // Keep the same values from the original
              }
            });
            return newTier;
          }) || [],
        },
      };
    };

    // Clone the exact structure of Season 1 - all demand types with all their periods
    let clonedPeriods = [];
    if (seasonsList.length > 0 && seasonsList[0].periods && seasonsList[0].periods.length > 0) {
      const firstSeasonPeriods = seasonsList[0].periods;
      
      // Clone each demand type with its own periods (not just DT1's periods)
      clonedPeriods = firstSeasonPeriods.map((demandType, idx) => ({
        demandTypeId: getRandomInt(1, 9999),
        demandTypeName: `Demand Type ${idx + 1}`,
        weekDays: Array.isArray(demandType.weekDays) ? [...demandType.weekDays] : [],
        rate: demandType.rate.map(period => clonePeriodWithNewIds(period)), // Clone each DT's own periods
      }));
    } else {
      // Fallback to default two demand types
      clonedPeriods = [
        {
          demandTypeId: getRandomInt(1, 9999),
          demandTypeName: `Demand Type 1`,
          weekDays: [1, 2, 3, 4, 5],
          rate: [
            {
              tariffId: getRandomInt(1, 9999),
              name: "PERIOD 1",
              demandType: "MonthlyMax_US",
              demandCost: 0,
              minPowerValue: 0,
              maxPowerValue: "Infinity",
              timeSlots: [
                {
                  startTime: {
                    hour: 0,
                    minute: 0,
                  },
                  endTime: {
                    hour: 24,
                    minute: 0,
                  },
                },
              ],
              options: {
                tierType: "Off",
                tierBoundaries: [createTierBoundary()],
              },
            },
          ],
        },
        {
          demandTypeId: getRandomInt(1, 9999),
          demandTypeName: `Demand Type 2`,
          weekDays: [6, 7],
          rate: [
            {
              tariffId: getRandomInt(1, 9999),
              name: "PERIOD 1",
              demandType: "MonthlyMax_US",
              demandCost: 0,
              minPowerValue: 0,
              maxPowerValue: "Infinity",
              timeSlots: [
                {
                  startTime: {
                    hour: 0,
                    minute: 0,
                  },
                  endTime: {
                    hour: 24,
                    minute: 0,
                  },
                },
              ],
              options: {
                tierType: "Off",
                tierBoundaries: [createTierBoundary()],
              },
            },
          ],
        },
      ];
    }

    const newSeason1 = {
      seasonId: getRandomInt(1, 9999),
      name: `Season ${nextSeasonNumber}`,
      startDay: newSeasonStartDate,
      endDay: newSeasonEndDate,
      seasonStartTime: {
        hour: 0,
        minute: 0,
      },
      seasonEndTime: {
        hour: 24,
        minute: 0,
      },
      rateMatrix: [],
      holidays: false,
      periods: clonedPeriods,
      isNcCost: false,
      ncCost: "",
    };

    const previousSeasonEndDate = moment(nextSeasonStart).subtract(1, "days").toDate();
    const updatedCurrentSeasons = seasonsList.map((season, index) =>
      index === seasonsList.length - 1
        ? { ...season, endDay: formatDate(previousSeasonEndDate) }
        : season
    );

    // Update states synchronously to avoid stale state issues
    const updatedSeasonsList = [...updatedCurrentSeasons, newSeason1];
    setSeasonsList(updatedSeasonsList);
    setActiveSeason(updatedSeasonsList.length - 1);
    setActiveDemandType(0);
    setSelectedSeason(updatedSeasonsList[updatedSeasonsList.length - 1]);
    setIsNcCost(false);
    setNcCost("");
  };

  const handleSeasonClick = (index) => {
    setActiveSeason(index);
    setSelectedSeason(seasonsList[index]);
    setIsNcCost(!!seasonsList[index].isNcCost);
    setNcCost(seasonsList[index].ncCost || "");
    setActiveDemandType(0);
  };

  const removeSeason = (seasonId) => {
    if (isReadOnly) return;
    setSeasonToRemove(seasonId);
    setShowConfirmRemoveSeason(true);
  };

  const confirmRemoveSeason = () => {
    if (!seasonToRemove) return;

    setSeasonsList((prevSeasonsList) => {
      const removedSeasonIndex = prevSeasonsList.findIndex(
        (season) => season.seasonId === seasonToRemove
      );
      let updatedSeasonsList = prevSeasonsList.filter(
        (season) => season.seasonId !== seasonToRemove
      );

      if (
        removedSeasonIndex > 0 &&
        removedSeasonIndex < prevSeasonsList.length - 1
      ) {
        const previousSeasonIndex = removedSeasonIndex - 1;
        const nextSeasonAfterRemoved = updatedSeasonsList[removedSeasonIndex];

        if (nextSeasonAfterRemoved?.startDay) {
          const updatedPreviousEndDay = moment(
            nextSeasonAfterRemoved.startDay,
            "DD-MM-YYYY",
            true
          )
            .subtract(1, "days")
            .format("DD-MM-YYYY");

          updatedSeasonsList[previousSeasonIndex] = {
            ...updatedSeasonsList[previousSeasonIndex],
            endDay: updatedPreviousEndDay,
          };
        }
      }

      if (
        removedSeasonIndex === prevSeasonsList.length - 1 &&
        updatedSeasonsList.length > 0
      ) {
        const newLastIndex = updatedSeasonsList.length - 1;
        const newLastSeason = updatedSeasonsList[newLastIndex];
        const endOfYearForSeason = moment(
          newLastSeason?.startDay,
          "DD-MM-YYYY",
          true
        );

        if (endOfYearForSeason.isValid()) {
          updatedSeasonsList[newLastIndex] = {
            ...newLastSeason,
            endDay: endOfYearForSeason.endOf("year").format("DD-MM-YYYY"),
          };
        }
      }

      // If only one season remains, set its endDay to default (one year minus one day from startDay)
      if (updatedSeasonsList.length === 1) {
        const season = updatedSeasonsList[0];
        const [day, month, year] = season.startDay.split("-");
        const startDate = new Date(`${year}-${month}-${day}`);
        const defaultEndDate = moment(startDate).add(1, "years").subtract(1, "days").toDate();
        const pad = (n) => n.toString().padStart(2, "0");
        const endYear = defaultEndDate.getFullYear();
        const endMonth = pad(defaultEndDate.getMonth() + 1);
        const endDay = pad(defaultEndDate.getDate());
        season.endDay = `${endDay}-${endMonth}-${endYear}`;
        updatedSeasonsList[0] = { ...season };
      }
      const newActiveSeasonIndex =
        updatedSeasonsList?.length > 0 ? updatedSeasonsList?.length - 1 : 0;
      setActiveSeason(newActiveSeasonIndex);
      setSelectedSeason(updatedSeasonsList?.[newActiveSeasonIndex] || null);
      return updatedSeasonsList;
    });
    props?.onChange?.();
    setShowConfirmRemoveSeason(false);
    setSeasonToRemove(null);
  };

  const handleDoubleClick = (index, item) => {
    if (isReadOnly) return;
    setEditingIndex(index);
    setNewItemValue(item.name);
  };

  const handleInputChange = (e) => {
    if (isReadOnly) return;
    setNewItemValue(e.target.value);
    props?.onChange?.();
  };

  const handleInputBlur = (index) => {
    if (isReadOnly) return;
    const updatedSeasonsList = [...seasonsList];
    updatedSeasonsList[index].name = newItemValue;
    setSeasonsList(updatedSeasonsList);
    setEditingIndex(null);
    props?.onChange?.();
  };

  const handleDateChange = (date) => {
    if (isReadOnly) return;
    if (!date) return;
    if (isFirstSeason()) return; // Prevent any changes to Season 1's date
    props?.onChange?.();

    const normalizedDate = new Date(date.getFullYear(), date.getMonth(), 1);
    
    const pad = (n) => n.toString().padStart(2, "0");
    const year = normalizedDate.getFullYear();
    const month = pad(normalizedDate.getMonth() + 1);
    const day = pad(normalizedDate.getDate());
    const formattedDate = `${day}-${month}-${year}`;
    
    // Only for non-first seasons: Calculate previous season's endDay as one day before new startDay
    let prevSeasonEndDate = moment(`${year}-${month}-${day}`, "YYYY-MM-DD").subtract(1, "days").toDate();
    const prevEndYear = prevSeasonEndDate.getFullYear();
    const prevEndMonth = pad(prevSeasonEndDate.getMonth() + 1);
    const prevEndDay = pad(prevSeasonEndDate.getDate());
    const updatedSeason = { ...selectedSeason, startDay: formattedDate };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );
    if (updatedSeasonsList.length > 1) {
      const currentSeasonIndex = updatedSeasonsList.findIndex(
        (season) => season.seasonId === updatedSeason.seasonId
      );
      const previousSeasonIndex = currentSeasonIndex - 1;
      if (previousSeasonIndex >= 0) {
        updatedSeasonsList[previousSeasonIndex].endDay = `${prevEndDay}-${prevEndMonth}-${prevEndYear}`;
      }

      if (currentSeasonIndex >= 0) {
        const [fd, fm, fy] = updatedSeasonsList[0].startDay.split("-");
        const firstSeasonYearEnd = new Date(Number(fy), 11, 31);
        const nextSeason = updatedSeasonsList[currentSeasonIndex + 1];
        if (nextSeason?.startDay) {
          const nextStartDate = parseDdMmYyyy(nextSeason.startDay);
          updatedSeasonsList[currentSeasonIndex].endDay = formatDate(
            moment(nextStartDate).subtract(1, "days").toDate()
          );
        } else {
          updatedSeasonsList[currentSeasonIndex].endDay = formatDate(firstSeasonYearEnd);
        }
      }
    }
    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
  };



  const handleDemandTypeClick = (index) => {
    setActiveDemandType(index);
  };
  const handleDemandTypeDoubleClick = (index, item) => {
    if (isReadOnly) return;
    setEditingDemandTypeIndex(index);
    setNewDemandTypeValue(item.demandTypeName);
  };
  const handleDemandTypeInputChange = (e) => {
    if (isReadOnly) return;
    setNewDemandTypeValue(e.target.value);
    props?.onChange?.();
  };
  const handleDemandTypeInputBlur = (index) => {
    if (isReadOnly) return;
    const updatedDemandTypes = [...selectedSeason.periods];
    updatedDemandTypes[index].demandTypeName = newDemandTypeValue;
    const updatedSeason = { ...selectedSeason, periods: updatedDemandTypes };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );
    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setEditingDemandTypeIndex(null);
    props?.onChange?.();
  };

  const addDemandType = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Only require at least one weekday to be present
    const allWeekdays = [1, 2, 3, 4, 5, 6, 7];
    let usedWeekdays = [];
    selectedSeason.periods.forEach((demandType) => {
      usedWeekdays = usedWeekdays.concat(demandType.weekDays);
    });
    // If all weekdays are already assigned, prevent adding new demand type
    const uniqueUsedWeekdays = Array.from(new Set(usedWeekdays));
    if (uniqueUsedWeekdays.length === 7) {
      callSnackbar(
        "error",
        t("demandCharge.errors.removeWeekdayToAddDemandType")
      );
      return;
    }
    const unusedWeekdays = allWeekdays.filter((day) => !usedWeekdays.includes(day));
    const nextDemandTypeNumber = selectedSeason.periods.length + 1;

    // Helper function to clone a period with new IDs and keep all values
    const clonePeriodWithNewIds = (period) => {
      return {
        ...period,
        tariffId: getRandomInt(1, 9999),
        options: {
          ...period.options,
          tierBoundaries: period.options?.tierBoundaries?.map(tier => {
            const newTier = { tierId: getRandomInt(1, 9999) };
            Object.keys(tier).forEach(key => {
              if (key !== "tierId") {
                newTier[key] = tier[key]; // Keep the same values
              }
            });
            return newTier;
          }) || [],
        },
      };
    };

    // Clone all periods from Season 1, Demand Type 1 (including all values like demandCost, timeSlots)
    let clonedRate = [];
    const season1 = seasonsList[0];
    if (season1 && season1.periods && season1.periods[0] && Array.isArray(season1.periods[0].rate)) {
      const basePeriods = season1.periods[0].rate;
      clonedRate = basePeriods.map(period => clonePeriodWithNewIds(period));
    } else {
      // Fallback if Season 1 DT1 doesn't exist yet
      const firstDemandType = selectedSeason.periods[0];
      if (firstDemandType && Array.isArray(firstDemandType.rate) && firstDemandType.rate[0]) {
        const basePeriod = firstDemandType.rate[0];
        const clonedTiers = (basePeriod.options?.tierBoundaries || []).map(tier => {
          const newTier = { tierId: getRandomInt(1, 9999) };
          Object.keys(tier).forEach(key => {
            if (key !== "tierId") {
              // For ratchet (key 7), preserve the value or use default 100
              const value = key === "7" ? (tier[key]?.value || DEMAND_NOTICE[7]?.value || 100) : "";
              newTier[key] = { name: tier[key]?.name, value };
            }
          });
          return newTier;
        });
        clonedRate = [
          {
            tariffId: getRandomInt(1, 9999),
            name: "PERIOD 1",
            demandType: basePeriod.demandType || "MonthlyMax_US",
            demandCost: 0,
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [],
            options: {
              tierType: basePeriod.options?.tierType || "Off",
              tierBoundaries: clonedTiers,
            },
          },
        ];
      } else {
        // Fallback to default single tier
        clonedRate = [
          {
            tariffId: getRandomInt(1, 9999),
            name: "PERIOD 1",
            demandType: "MonthlyMax_US",
            demandCost: 0,
            minPowerValue: 0,
            maxPowerValue: "Infinity",
            timeSlots: [],
            options: {
              tierType: "Off",
              tierBoundaries: [createTierBoundary()],
            },
          },
        ];
      }
    }

    const newDemandType = {
      demandTypeId: getRandomInt(1, 9999),
      demandTypeName: `Demand Type ${nextDemandTypeNumber}`,
      rate: clonedRate,
      weekDays: unusedWeekdays, // Assign ALL unused weekdays to the new demand type
    };

    // Update current selected season
    const updatedDemandTypes = [...selectedSeason.periods, newDemandType];
    const updatedSeason = { ...selectedSeason, periods: updatedDemandTypes };
    
    // Replicate the new demand type to all other seasons
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === updatedSeason.seasonId) {
        // This is the current season - use the updated version with new demand type
        return updatedSeason;
      } else {
        // For other seasons, add the new demand type only if it doesn't already exist
        const demandTypeExists = season.periods.some(dt => dt.demandTypeName === newDemandType.demandTypeName);
        if (!demandTypeExists) {
          // Clone the new demand type with fresh IDs for this season
          const replicatedDemandType = {
            ...newDemandType,
            demandTypeId: getRandomInt(1, 9999),
            rate: newDemandType.rate.map(period => ({
              ...period,
              tariffId: getRandomInt(1, 9999),
              options: {
                ...period.options,
                tierBoundaries: period.options?.tierBoundaries?.map(tier => ({
                  ...tier,
                  tierId: getRandomInt(1, 9999),
                })) || [],
              },
            })),
          };
          return {
            ...season,
            periods: [...season.periods, replicatedDemandType],
          };
        }
        return season;
      }
    });

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setActiveDemandType(updatedDemandTypes.length - 1);
  };

  const removeDemandType = (demandTypeId) => {
    if (isReadOnly) return;
    setDemandTypeToRemove(demandTypeId);
    setShowConfirmRemoveDemandType(true);
  };

  const confirmRemoveDemandType = () => {
    if (!demandTypeToRemove) return;

    const updatedDemandTypes = selectedSeason.periods.filter(
      (demandType) => demandType.demandTypeId !== demandTypeToRemove
    );
    const updatedSeason = { ...selectedSeason, periods: updatedDemandTypes };
    const updatedSeasonsList = seasonsList.map((season) =>
      season.seasonId === updatedSeason.seasonId ? updatedSeason : season
    );

    setSeasonsList(updatedSeasonsList);
    setSelectedSeason(updatedSeason);
    setActiveDemandType(updatedDemandTypes.length > 0 ? 0 : null);

    setShowConfirmRemoveDemandType(false);
    setDemandTypeToRemove(null);
    props?.onChange?.();
  };

  const handleWeekdayClick = (dayId) => {
    if (isReadOnly) return;
    
    const updatedDemandType = [...selectedSeason.periods];
    const weekDays = updatedDemandType[activeDemandType].weekDays;
    props?.onChange?.();
    if (weekDays.includes(dayId)) {
      // Remove weekday directly without confirmation
      const newWeekDays = weekDays.filter((id) => id !== dayId);
      
      if (newWeekDays.length === 0) {
        callSnackbar("error", t("demandCharge.errors.atLeastOneWeekday"));
        return;
      }
      
      updatedDemandType[activeDemandType].weekDays = newWeekDays;
      setSelectedSeason({ ...selectedSeason, periods: updatedDemandType });
      return;
    }
    
    // Add weekday
    // Check if this weekday is already selected in any other demand type
    const isUsed = updatedDemandType.some((dt, idx) =>
      idx !== activeDemandType && dt.weekDays.includes(dayId)
    );
    if (isUsed) {
      callSnackbar("error", t("demandCharge.errors.weekdayAlreadySelected"));
      return;
    }
    
    const newWeekDays = [...weekDays, dayId];
    updatedDemandType[activeDemandType].weekDays = newWeekDays;
    setSelectedSeason({ ...selectedSeason, periods: updatedDemandType });
  };



  const addPeriodToDemandType = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Only allow adding periods in master context (first season, first demand type)
    if (!isMasterContext) {
      callSnackbar("error", t("demandCharge.errors.cannotAddPeriodNotMaster") || "You can only add periods in the first Demand Type of the first Season");
      return;
    }
    // Check if all periods' time slots already cover 24 hours
    const activePeriods = selectedSeason?.periods?.[activeDemandType];
    if (activePeriods) {
      // Flatten all time slots from all rates in the active demand type
      const allTimeSlots = activePeriods.rate.flatMap(tariff => tariff.timeSlots);
      if (allTimeSlots.length > 0) {
        // Sort time slots by start time
        allTimeSlots.sort((a, b) => {
          const startA = a.startTime.hour * 60 + a.startTime.minute;
          const startB = b.startTime.hour * 60 + b.startTime.minute;
          return startA - startB;
        });
        const firstSlot = allTimeSlots[0];
        const lastSlot = allTimeSlots[allTimeSlots.length - 1];
        const startsAtZero = firstSlot.startTime.hour === 0 && firstSlot.startTime.minute === 0;
        const endsAt24 = lastSlot.endTime.hour === 24 && lastSlot.endTime.minute === 0;
        // Check for contiguous slots
        let contiguous = true;
        for (let i = 0; i < allTimeSlots.length - 1; i++) {
          const end = allTimeSlots[i].endTime.hour * 60 + allTimeSlots[i].endTime.minute;
          const nextStart = allTimeSlots[i + 1].startTime.hour * 60 + allTimeSlots[i + 1].startTime.minute;
          if (end !== nextStart) {
            contiguous = false;
            break;
          }
        }
        // if (startsAtZero && endsAt24 && contiguous) {
        //   callSnackbar("error", t("demandCharge.errors.cannotAddPeriodFullDay"));
        //   return;
        // }
      }
    }
    // Build the new period structure with empty values for tiers from the master context
    const lastPeriod = selectedSeason.periods[activeDemandType].rate[selectedSeason.periods[activeDemandType].rate.length - 1];
    const lastTierBoundaries = lastPeriod.options.tierBoundaries;
    const newTierBoundaries = lastTierBoundaries.map(tier => {
      const keys = Object.keys(tier).filter(k => k !== "tierId");
      const newTier = { tierId: getRandomInt(1, 9999) };
      keys.forEach(key => {
        // Copy value from previous tier
        // For ratchet (key 7), preserve the value or use default 100
        let value = tier[key]?.value || "";
        if (key === "7") {
          value = tier[key]?.value || DEMAND_NOTICE[7]?.value || 100;
        }
        newTier[key] = { name: DEMAND_NOTICE[key]?.name, value };
      });
      return newTier;
    });
    const newPeriod = {
      tariffId: getRandomInt(1, 9999),
      name: `PERIOD ${selectedSeason.periods[activeDemandType].rate.length + 1}`,
      timeSlots: [],
      options: {
        tierType: "Off",
        tierBoundaries: newTierBoundaries,
      },
    };
    
    // Create the new seasons list with the period added to ALL demand types in ALL seasons
    const newSeasonsList = seasonsList.map((season) => {
      return {
        ...season,
        periods: season.periods.map((demandType, dtIndex) => {
          // Replicate to all demand types - create new tier IDs and preserve ratchet values
          const replicatedTierBoundaries = lastTierBoundaries.map(tier => {
            const keys = Object.keys(tier).filter(k => k !== "tierId");
            const newTier = { tierId: getRandomInt(1, 9999) };
            keys.forEach(key => {
              // Copy value from previous tier
              // For ratchet (key 7), preserve the value or use default 100
              let value = tier[key]?.value || "";
              if (key === "7") {
                value = tier[key]?.value || DEMAND_NOTICE[7]?.value || 100;
              }
              newTier[key] = { name: DEMAND_NOTICE[key]?.name, value };
            });
            return newTier;
          });
          const replicatedPeriod = {
            tariffId: getRandomInt(1, 9999),
            name: newPeriod.name,
            timeSlots: [],
            options: {
              tierType: "Off",
              tierBoundaries: replicatedTierBoundaries,
            },
          };
          return {
            ...demandType,
            rate: [...demandType.rate, replicatedPeriod],
          };
        }),
      };
    });
    
    // Update both states with the new data
    setSeasonsList(newSeasonsList);
    
    // Find and update the selectedSeason from the new list
    const updatedSelectedSeason = newSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
    
    setCurrentDate(new Date());
  };

 const addTierToDemandType = () => {
   if (isReadOnly) return;
   props?.onChange?.();
    // Check for max 30 tiers in active demand type's first period
    const tierCount = selectedSeason?.periods?.[activeDemandType]?.rate?.[0]?.options?.tierBoundaries?.length || 0;
    if (tierCount >= 30) {
      callSnackbar("error", t("demandCharge.errors.maxTiers"));
      return;
    }
    setSeasonsList((prevSeasons) => {
      const newSeasons = prevSeasons.map((season) => {
        return {
          ...season,
          periods: season.periods.map((demandType) => {
            const lastPeriod = demandType.rate[0];
            const lastTier = lastPeriod.options.tierBoundaries[lastPeriod.options.tierBoundaries.length - 1];
            const presentKeys = Object.keys(lastTier).filter(k => k !== "tierId" && k !== "3" && k !== "4");
            const newTier = { tierId: getRandomInt(1, 999999) };
            presentKeys.forEach(key => {
              // Copy value from previous tier
              // For ratchet (key 7), preserve the value or use default 100
              let value = lastTier[key]?.value || "";
              if (key === "7") {
                value = lastTier[key]?.value || DEMAND_NOTICE[7]?.value || 100;
              }
              newTier[key] = { name: DEMAND_NOTICE[key]?.name, value };
            });
            return {
              ...demandType,
              rate: demandType.rate.map((period) => {
                const uniqueTier = { ...newTier, tierId: getRandomInt(1, 999999) };
                return {
                  ...period,
                  options: {
                    ...period.options,
                    tierBoundaries: [...period.options.tierBoundaries, uniqueTier],
                  },
                };
              }),
            };
          }),
        };
      });
      // Also update selectedSeason to the latest reference from newSeasons
      const updated = newSeasons.find(s => s.seasonId === selectedSeason.seasonId);
      if (updated) setSelectedSeason(updated);
      return newSeasons;
    });
    setCurrentDate(new Date());
  };

  const removePeriodFromDemandType = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    // Only allow removing periods in master context (first season, first demand type)
    if (!isMasterContext) {
      callSnackbar("error", t("demandCharge.errors.cannotRemovePeriodNotMaster") || "You can only remove periods in the first Demand Type of the first Season");
      return;
    }

    // Check if there is only one period before removing
    const activeGroup = selectedSeason.periods[activeDemandType];
    if (activeGroup.rate.length === 1) {
      callSnackbar("error", t("demandCharge.errors.cannotRemoveLastPeriod"));
      return;
    }

    // Create the new seasons list with the last period removed from ALL demand types in ALL seasons
    const newSeasonsList = seasonsList.map((season) => {
      return {
        ...season,
        periods: season.periods.map((demandType) => {
          // Remove the last period if it exists and has more than one period
          if (demandType.rate.length > 1) {
            return {
              ...demandType,
              rate: demandType.rate.slice(0, -1),
            };
          }
          return demandType;
        }),
      };
    });
    
    // Update both states with the new data
    setSeasonsList(newSeasonsList);
    
    // Find and update the selectedSeason from the new list
    const updatedSelectedSeason = newSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
    
    setCurrentDate(new Date());
  };

  const removeTierfromDemandType = () => {
    if (isReadOnly) return;
    props?.onChange?.();
    setSeasonsList((prevSeasons) => {
      // Check if any demand type in any season has only one tier left in any period
      if (prevSeasons.some(season => season.periods.some(dt => dt.rate.some(period => period.options.tierBoundaries.length === 1)))) {
        callSnackbar("error", t("demandCharge.errors.cannotRemoveLastTier"));
        return prevSeasons;
      }
      const newSeasons = prevSeasons.map((season) => {
        return {
          ...season,
          periods: season.periods.map((demandType) => {
            return {
              ...demandType,
              rate: demandType.rate.map((period) => {
                return {
                  ...period,
                  options: {
                    ...period.options,
                    tierBoundaries: period.options.tierBoundaries.slice(0, -1),
                  },
                };
              }),
            };
          }),
        };
      });
      // Also update selectedSeason to the latest reference from newSeasons
      const updated = newSeasons.find(s => s.seasonId === selectedSeason.seasonId);
      if (updated) setSelectedSeason(updated);
      return newSeasons;
    });
    setCurrentDate(new Date());
  };
  const updateTierInDayGroup = (
    periodIndex,
    tierIndex,
    key,
    value
  ) => {
    setSelectedSeason((prevSelectedSeason) => {
      const updatedDemandType = [...prevSelectedSeason.periods];
      const activeGroup = updatedDemandType[activeDemandType];
      activeGroup.rate[periodIndex].options.tierBoundaries = activeGroup.rate[
        periodIndex
      ].options.tierBoundaries.map((tier, index) =>
        index === tierIndex ? { ...tier, [key]: value } : tier
      );
      return { ...prevSelectedSeason, periods: updatedDemandType };
    });
    props?.onChange?.();
    setCurrentDate(new Date());
  };

  const updateTierFractionInDayGroup = (periodIndex, tierIndex, key, value) => {
    setSelectedSeason((prevSelectedSeason) => {
      const updatedDemandType = [...prevSelectedSeason.periods];
      const activeGroup = updatedDemandType[activeDemandType];
      activeGroup.rate[periodIndex].options.tierBoundaries = activeGroup.rate[periodIndex].options.tierBoundaries.map((tier, index) => {
        if (index === tierIndex) {
          return {
            ...tier,
            [key]: { name: DEMAND_NOTICE[key].name, value: value }
          };
        }
        return tier;
      });
      return { ...prevSelectedSeason, periods: updatedDemandType };
    });
    props?.onChange?.();
    setCurrentDate(new Date());
  };

  const updateTimeSlot = (periodIndex, newStartTime, newEndTime) => {
    // Update seasonsList first (source of truth)
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === selectedSeason.seasonId) {
        const updatedPeriods = season.periods.map((demandType, dtIndex) => {
          if (dtIndex === activeDemandType) {
            return {
              ...demandType,
              rate: demandType.rate.map((period, pIndex) => {
                if (pIndex === periodIndex) {
                  return {
                    ...period,
                    timeSlots: [
                      ...(period.timeSlots || []),
                      {
                        startTime: newStartTime,
                        endTime: newEndTime,
                      },
                    ],
                  };
                }
                return period;
              }),
            };
          }
          return demandType;
        });
        return { ...season, periods: updatedPeriods };
      }
      return season;
    });
    props?.onChange?.();
    // Update both states synchronously
    setSeasonsList(updatedSeasonsList);
    const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
  };

  const updatePeriodName = (periodIndex, newPeriodName) => {
    // Update seasonsList first (source of truth)
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === selectedSeason.seasonId) {
        const updatedPeriods = season.periods.map((demandType, dtIndex) => {
          if (dtIndex === activeDemandType) {
            return {
              ...demandType,
              rate: demandType.rate.map((period, pIndex) => {
                if (pIndex === periodIndex) {
                  return { ...period, name: newPeriodName };
                }
                return period;
              }),
            };
          }
          return demandType;
        });
        return { ...season, periods: updatedPeriods };
      }
      return season;
    });
    props?.onChange?.();
    
    // Update both states synchronously
    setSeasonsList(updatedSeasonsList);
    const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
  };

  const usedWeekdays = getUsedWeekdays(selectedSeason, activeDemandType);

  const handleStartTimeChange = (value) => {
    if (!value) return;
    const [hour, minute] = value.split(":").map(Number);
    setSelectedSeason((prevSeason) => ({
      ...prevSeason,
      seasonStartTime: { hour, minute },
    }));
    setSeasonsList(prevList => prevList.map((season, idx) =>
      idx === activeSeason
        ? { ...season, seasonStartTime: { hour, minute } }
        : season
    ));
  };

  // Dynamic date constraints per selected season similar to MGTariff
  const dateConstraints = (() => {
    if (!selectedSeason || seasonsList.length === 0) return {};
    const index = seasonsList.findIndex(s => s.seasonId === selectedSeason.seasonId);
    if (index === -1) return {};
    
    // Get the first season's start date as the base for the 1-year window
    const firstSeason = seasonsList[0];
    const [fd, fm, fy] = firstSeason.startDay.split('-');
    const firstSeasonStart = new Date(`${fy}-${fm}-${fd}`);
    
    // Calculate the 1-year window end (1 year from first season start - 1 day)
    const globalEnd = moment(firstSeasonStart).add(1, 'years').subtract(1, 'days').toDate();
    
    // For the first season, no minimum date constraint
    let minDate = null;
    
    // For non-first seasons, must start after previous season's end
    if (index > 0) {
      const prev = seasonsList[index - 1];
      if (prev?.startDay) {
        const [pd, pm, py] = prev.startDay.split('-');
        const prevStart = new Date(`${py}-${pm}-${pd}`);
        minDate = moment(prevStart).add(1, 'months').startOf('month').toDate();
      }
    }
    
    // Maximum date is constrained by: next season's start (if exists) OR the 1-year window end
    let maxDate = globalEnd;
    if (index < seasonsList.length - 1) {
      const next = seasonsList[index + 1];
      if (next?.startDay) {
        const [nd, nm, ny] = next.startDay.split('-');
        const nextStart = new Date(`${ny}-${nm}-${nd}`);
        const nextMaxDate = moment(nextStart).subtract(1, 'months').startOf('month').toDate();
        // Use the earlier of the two constraints
        maxDate = moment(nextMaxDate).isBefore(globalEnd) ? nextMaxDate : globalEnd;
      }
    }

    maxDate = moment(maxDate).startOf('month').toDate();
    
    return { minDate, maxDate };
  })();

  const removeTimeSlotIndex = (tariff, tariffId, timeSlotIndex) => {
    if (tariff.tariffId === tariffId) {
      // Remove the timeSlot at the specified timeSlotIndex
      const updatedTimeSlots = tariff?.timeSlots?.filter(
        (_, index) => index !== timeSlotIndex
      );
      return { ...tariff, timeSlots: updatedTimeSlots };
    }
    return tariff;
  };

  const handleRemoveTimeSlots = (timeSlotIndex, tariffId) => {
    // Update seasonsList first (source of truth)
    props?.onChange?.();
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === selectedSeason.seasonId) {
        const updatedPeriods = season.periods.map((demandType, dtIndex) => {
          if (dtIndex === activeDemandType) {
            return {
              ...demandType,
              rate: demandType.rate.map((tariff) => {
                if (tariff.tariffId === tariffId) {
                  // Remove the timeSlot at the specified timeSlotIndex
                  const updatedTimeSlots = tariff?.timeSlots?.filter(
                    (_, index) => index !== timeSlotIndex
                  );
                  
                  // Preserve ratchet value - if enabled but empty, restore to 100
                  const updatedTierBoundaries = tariff?.options?.tierBoundaries?.map((tier) => {
                    if (tier["7"]) {
                      // Ratchet is enabled, if value is empty, restore to default (100)
                      return {
                        ...tier,
                        "7": {
                          name: tier["7"].name || DEMAND_NOTICE[7]?.name,
                          value: tier["7"].value || DEMAND_NOTICE[7]?.value || 100
                        }
                      };
                    }
                    return tier;
                  });
                  
                  return {
                    ...tariff,
                    timeSlots: updatedTimeSlots,
                    options: {
                      ...tariff.options,
                      tierBoundaries: updatedTierBoundaries || tariff.options?.tierBoundaries
                    }
                  };
                }
                return tariff;
              }),
            };
          }
          return demandType;
        });
        return { ...season, periods: updatedPeriods };
      }
      return season;
    });
    
    // Update both states synchronously
    setSeasonsList(updatedSeasonsList);
    const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
  };

  const selectedSeasonDayGroupUpdate = (rate, tierIndex, key, checked) => {
    // Only update the tier at tierIndex in this rate's tierBoundaries
    const updatedTierBoundaries = rate?.options?.tierBoundaries.map(
      (tier, idx) => {
        if (idx === tierIndex) {
          const updatedTier = { ...tier };
          if (checked) {
            updatedTier[key] = { name: DEMAND_NOTICE[key]?.name, value: "" };
          } else {
            delete updatedTier[key];
          }
          return updatedTier;
        }
        return tier;
      }
    );
    props?.onChange?.();
    return {
      ...rate,
      options: { ...rate.options, tierBoundaries: updatedTierBoundaries },
    };
  };

  const updateSelectedSeasonAndDayGroup = (tierIndex, key, checked) => {
    // Update seasonsList first (source of truth)
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === selectedSeason.seasonId) {
        const updatedPeriods = season.periods.map((demandType, dtIndex) => {
          if (dtIndex === activeDemandType) {
            return {
              ...demandType,
              rate: demandType.rate.map((rate) => {
                return selectedSeasonDayGroupUpdate(rate, tierIndex, key, checked);
              }),
            };
          }
          return demandType;
        });
        return { ...season, periods: updatedPeriods };
      }
      return season;
    });
    props?.onChange?.();
    // Update both states synchronously
    setSeasonsList(updatedSeasonsList);
    const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
  };

    const batchUpdateTierFractionsInDayGroup = (changes) => {
      // Update selectedSeason
      setSelectedSeason((prevSelectedSeason) => {
        const updatedDemandType = [...prevSelectedSeason.periods];
        const activeGroup = updatedDemandType[activeDemandType];
        // Build a map of changes for quick lookup
        const changeMap = {};
        changes.forEach(({ periodIndex, tierIndex, key, value }) => {
          if (!changeMap[periodIndex]) changeMap[periodIndex] = {};
          if (!changeMap[periodIndex][tierIndex]) changeMap[periodIndex][tierIndex] = {};
          changeMap[periodIndex][tierIndex][key] = value;
        });
        activeGroup.rate.forEach((tariff, periodIndex) => {
          tariff.options.tierBoundaries.forEach((tier, tierIndex) => {
            if (changeMap[periodIndex] && changeMap[periodIndex][tierIndex]) {
              Object.entries(changeMap[periodIndex][tierIndex]).forEach(([key, value]) => {
                tier[key] = { name: DEMAND_NOTICE[key].name, value };
              });
            }
          });
        });
        return { ...prevSelectedSeason, periods: updatedDemandType };
      });
      props?.onChange?.();
      // Update seasonsList as well
      setSeasonsList((prevSeasonsList) => {
        return prevSeasonsList.map((season) => {
          if (season.seasonId !== selectedSeason.seasonId) return season;
          const updatedPeriods = [...season.periods];
          const activeGroup = updatedPeriods[activeDemandType];
          // Build a map of changes for quick lookup
          const changeMap = {};
          changes.forEach(({ periodIndex, tierIndex, key, value }) => {
            if (!changeMap[periodIndex]) changeMap[periodIndex] = {};
            if (!changeMap[periodIndex][tierIndex]) changeMap[periodIndex][tierIndex] = {};
            changeMap[periodIndex][tierIndex][key] = value;
          });
          activeGroup.rate.forEach((tariff, periodIndex) => {
            tariff.options.tierBoundaries.forEach((tier, tierIndex) => {
              if (changeMap[periodIndex] && changeMap[periodIndex][tierIndex]) {
                Object.entries(changeMap[periodIndex][tierIndex]).forEach(([key, value]) => {
                  tier[key] = { name: DEMAND_NOTICE[key].name, value };
                });
              }
            });
          });
          return { ...season, periods: updatedPeriods };
        });
      });
      setCurrentDate(new Date());
    };

        const batchUpdateRachetGlobally = (tierIdToSync, newValue) => {
      // Update seasonsList with new Rachet value for this tierId across ALL seasons and demand types
      setSeasonsList((prevSeasonsList) => {
        return prevSeasonsList.map((season) => {
          const updatedPeriods = season.periods.map((demandType) => {
            return {
              ...demandType,
              rate: demandType.rate.map((period) => {
                return {
                  ...period,
                  options: {
                    ...period.options,
                    tierBoundaries: period.options.tierBoundaries.map((tier) => {
                      // if (tier.tierId === tierIdToSync) {
                        return {
                          ...tier,
                          "7": { name: "Rachet Demand", value: newValue }
                        };
                      // }
                      // return tier;
                    })
                  }
                };
              })
            };
          });
          return { ...season, periods: updatedPeriods };
        });
      });
      props?.onChange?.();
      // Update selectedSeason as well
      setSelectedSeason((prevSelectedSeason) => {
        const updatedPeriods = prevSelectedSeason.periods.map((demandType) => {
          return {
            ...demandType,
            rate: demandType.rate.map((period) => {
              return {
                ...period,
                options: {
                  ...period.options,
                  tierBoundaries: period.options.tierBoundaries.map((tier) => {
                    // if (tier.tierId === tierIdToSync) {
                      return {
                        ...tier,
                        "7": { name: "Rachet Demand", value: newValue }
                      };
                    //  }
                    // return tier;
                  })
                }
              };
            })
          };
        });
        return { ...prevSelectedSeason, periods: updatedPeriods };
      });

      setCurrentDate(new Date());
    };

  // Sync selectedDemandNoticeOptions with tierBoundaries keys
  useEffect(() => {
    const activePeriods = selectedSeason?.periods?.[activeDemandType];
    if (activePeriods && activePeriods.rate[0]?.options?.tierBoundaries?.length > 0) {
      const keys = Object.keys(activePeriods.rate[0].options.tierBoundaries[0]).filter(k => k !== "tierId");
      setSelectedDemandNoticeOptions(keys.map(Number));
    }
  }, [selectedSeason, activeDemandType]);

  // Sync master context (Season 1, DT 1) demand notice keys to ALL seasons and ALL demand types
  useEffect(() => {
    if (!seasonsList.length || !seasonsList[0]?.periods?.length) return;
    const masterTier = seasonsList[0].periods[0]?.rate?.[0]?.options?.tierBoundaries?.[0];
    if (!masterTier) return;
    const masterKeys = new Set(Object.keys(masterTier).filter(k => k !== "tierId"));

    let needsUpdate = false;
    // Check if any non-master tier has extra or missing keys
    seasonsList.forEach((season, sIdx) => {
      season.periods.forEach((dt, dIdx) => {
        dt.rate.forEach((period) => {
          period.options.tierBoundaries.forEach((tier) => {
            const tierKeys = new Set(Object.keys(tier).filter(k => k !== "tierId"));
            masterKeys.forEach(k => { if (!tierKeys.has(k)) needsUpdate = true; });
            tierKeys.forEach(k => { if (!masterKeys.has(k)) needsUpdate = true; });
          });
        });
      });
    });

    if (!needsUpdate) return;

    const syncedSeasons = seasonsList.map((season, sIdx) => ({
      ...season,
      periods: season.periods.map((dt, dIdx) => {
        // Skip master context - it's the source of truth
        if (sIdx === 0 && dIdx === 0) return dt;
        return {
          ...dt,
          rate: dt.rate.map((period) => ({
            ...period,
            options: {
              ...period.options,
              tierBoundaries: period.options.tierBoundaries.map((tier) => {
                const newTier = { tierId: tier.tierId };
                // Add all master keys - keep existing value if present, else use default
                masterKeys.forEach(k => {
                  if (tier.hasOwnProperty(k)) {
                    newTier[k] = tier[k];
                  } else {
                    newTier[k] = { name: DEMAND_NOTICE[k]?.name, value: k === "7" ? 100 : "" };
                  }
                });
                return newTier;
              }),
            },
          })),
        };
      }),
    }));

    setSeasonsList(syncedSeasons);
    const updatedSelected = syncedSeasons.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelected) setSelectedSeason(updatedSelected);
  }, [seasonsList[0]?.periods?.[0]?.rate?.[0]?.options?.tierBoundaries]);

  const { type, message } = snackbarProps;
  const { colorScheme } = useMantineColorScheme();

  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  ); 

  const handleDemandNoticeOptionClick = (key) => {
    // Only allow click in master context, but replicate to all
    if (!isMasterContext) return;
    setSelectedDemandNoticeOptions((prev) => {
      // Toggle key in master context
      const newSelected = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Replicate structure to all demand types in all seasons (structure only, values unique)
      // (For now, just update the flat array, but logic is ready for 2D if needed)
      // Also update selectedSeason so the table reflects changes immediately
      setSelectedSeason((prevSelectedSeason) => {
        const updatedPeriods = prevSelectedSeason.periods.map((demandType) => {
          return {
            ...demandType,
            rate: demandType.rate.map((period) => {
              const updatedTiers = period.options.tierBoundaries.map((tier) => {
                const newTier = { ...tier };
                // Remove or add the key as in master context
                if (prev.includes(key)) {
                  if (newTier.hasOwnProperty(key)) delete newTier[key];
                } else {
                  if (!newTier.hasOwnProperty(key)) newTier[key] = { name: DEMAND_NOTICE[key]?.name, value: key === 7 ? 100 : "" };
                }
                return newTier;
              });
              return {
                ...period,
                options: {
                  ...period.options,
                  tierBoundaries: updatedTiers,
                },
              };
            }),
          };
        });
        return { ...prevSelectedSeason, periods: updatedPeriods };
      });
      setSeasonsList((prevSeasons) => {
        return prevSeasons.map((season, sIdx) => ({
          ...season,
          periods: season.periods.map((demandType, dIdx) => {
            return {
              ...demandType,
              rate: demandType.rate.map((period) => {
                const updatedTiers = period.options.tierBoundaries.map((tier) => {
                  const newTier = { ...tier };
                  // Remove or add the key as in master context
                  if (prev.includes(key)) {
                    if (newTier.hasOwnProperty(key)) delete newTier[key];
                  } else {
                    if (!newTier.hasOwnProperty(key)) newTier[key] = { name: DEMAND_NOTICE[key]?.name, value: key === 7 ? 100 : "" };
                  }
                  return newTier;
                });
                return {
                  ...period,
                  options: {
                    ...period.options,
                    tierBoundaries: updatedTiers,
                  },
                };
              }),
            };
          }),
        }));
      });
      return newSelected;
    });
  };

const removeOptionFromAllTiers = (key) => {
  // Remove from ALL seasons and ALL demand types (not just the current one)
  const updatedSeasonsList = seasonsList.map((season) => ({
    ...season,
    periods: season.periods.map((demandType) => ({
      ...demandType,
      rate: demandType.rate.map((period) => ({
        ...period,
        options: {
          ...period.options,
          tierBoundaries: period.options.tierBoundaries.map((tier) => {
            const newTier = { ...tier };
            if (newTier.hasOwnProperty(key)) {
              delete newTier[key];
            }
            return newTier;
          }),
        },
      })),
    })),
  }));
  props?.onChange?.();
  // Update both states synchronously
  setSeasonsList(updatedSeasonsList);
  const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
  if (updatedSelectedSeason) {
    setSelectedSeason(updatedSelectedSeason);
  }
  setSelectedDemandNoticeOptions((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : prev);
};

  // Calculate tierCount for the active demand type
  const tierCount = selectedSeason?.periods?.[activeDemandType]?.rate?.[0]?.options?.tierBoundaries?.length || 1;

  // Handler to update period color for a period (rowIndex) in the active demand type
  const updatePeriodColor = (rowIndex, color) => {
    // Update seasonsList first (source of truth)
    const updatedSeasonsList = seasonsList.map((season) => {
      if (season.seasonId === selectedSeason.seasonId) {
        const updatedPeriods = season.periods.map((demandType, dtIndex) => {
          if (dtIndex === activeDemandType) {
            return {
              ...demandType,
              rate: demandType.rate.map((period, pIndex) => {
                if (pIndex === rowIndex) {
                  return { ...period, periodColor: color };
                }
                return period;
              }),
            };
          }
          return demandType;
        });
        return { ...season, periods: updatedPeriods };
      }
      return season;
    });
    
    // Update both states synchronously
    setSeasonsList(updatedSeasonsList);
    const updatedSelectedSeason = updatedSeasonsList.find(s => s.seasonId === selectedSeason.seasonId);
    if (updatedSelectedSeason) {
      setSelectedSeason(updatedSelectedSeason);
    }
  };

  return (
    <>
      {/* Confirm Copy Modal (uses pendingCopySeasonId set by the inline select) */}
      <SEConfirmModal
        opened={showCopyConfirmModal}
        onClose={() => {
          setShowCopyConfirmModal(false);
          setPendingCopySeasonId(null);
          setCopyFromSeasonId(null);
          setCopyFromEnabled(false);
        }}
        onConfirm={() => {
          const id = pendingCopySeasonId || copyFromSeasonId;
          if (id?.startsWith("it-")) {
            const seasonId = id.replace("it-", "");
            const liveSeasons = props.getImportTariffSeasons?.() || props.importTariffSeasons || [];
            const importSeason = liveSeasons.find((s) => String(s.seasonId) === seasonId);
            copyRatesFromImportTariffSeason(importSeason);
          } else {
            const dcSeasonId = id?.startsWith("dc-") ? id.replace("dc-", "") : id;
            copyRatesFromSeason(dcSeasonId);
          }
          setShowCopyConfirmModal(false);
          setCopyFromEnabled(false);
          setCopyFromSeasonId(null);
          setPendingCopySeasonId(null);
        }}
        title={t('tariff.copySeasonConfirmTitle') || 'Copy Season'}
        message={(() => {
          const id = pendingCopySeasonId || copyFromSeasonId;
          let srcName;
          if (id?.startsWith("it-")) {
            const seasonId = id.replace("it-", "");
            const liveSeasons = props.getImportTariffSeasons?.() || props.importTariffSeasons || [];
            const importSeason = liveSeasons.find((s) => String(s.seasonId) === seasonId);
            srcName = importSeason?.name ? `${t('tariff.copyFromGroup.importTariff') || 'import tariff'} ${importSeason.name}` : undefined;
          } else {
            const dcSeasonId = id?.startsWith("dc-") ? id.replace("dc-", "") : id;
            const src = seasonsList.find((s) => String(s.seasonId) === String(dcSeasonId));
            srcName = src?.name ? `${t('tariff.copyFromGroup.demandCharge') || 'demand charge'} ${src.name}` : undefined;
          }
          const srcText = srcName ?? (t('tariff.copyFromDefault') || 'the selected season');
          const destText = selectedSeason?.name ?? 'current season';
          return (
            t('tariff.copySeasonConfirmMessage', { src: srcText, dest: destText }) ||
            `Do you want to copy all day group data from ${srcText} to ${destText}? This will replace existing data.`
          );
        })()}
        confirmLabel={t('common.confirm') || 'Confirm'}
        cancelLabel={t('common.cancel') || 'Cancel'}
      />
      <se-snackbar
        id={uniqueId}
        type={type}
        icon="information_stroke"
        message={message}
        duration="3000"
      ></se-snackbar>
      <div>
        <div style={{ position: "relative" }}>
          {/* Floating sunburst chart at top right */}
          {/* <div style={{ position: "absolute", top: "-100%", right: "15%", minWidth: 450, maxWidth: 500, zIndex: 10 }}>
            <SeasonSunburstChart seasonData={seasonsList} />
          </div> */}
          {/* Main content below, not affected by floating chart */}
          <DynamicTabbar
            items={seasonsList}
            activeIndex={activeSeason}
            editingIndex={editingIndex}
            newItemValue={newItemValue}
            handleItemClick={handleSeasonClick}
            handleDoubleClick={handleDoubleClick}
            handleInputChange={handleInputChange}
            handleInputBlur={handleInputBlur}
            removeItem={removeSeason}
            addItem={addSeason}
            itemKey="seasonId"
            itemName="name"
            itemId="seasonId"
            showAddRemove={!props.isVersionCompleted}
          />
        </div>
        <div className="mb-2">
          <div className="row">
            <div className="d-flex flex-column col-sm-8 col-md-8">
              <div
                className="row"
                style={{ marginTop: "10px", marginBottom: "10px" }}
              >
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex">
                  {" "}
                  {/* <span className="header-content-roman">I. </span> */}
                  <span className="header-content"> {t("demandCharge.labels.defineYearlySeason")}</span>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6 ">
                  {/* <div className="mantine-label-group" style={{ display: "flex", alignItems: "center", gap: 8 }}> */}
                    {/* <label style={{ fontWeight: 600, fontSize: 14, marginBottom: 0, marginRight: 8 }}>{t("demandCharge.labels.startDate")}</label> */}
                    <se-form-field
                    // padding="none"
                    // label-align="left"
                    // label-width="30%"
                    required={true}
                    type="date"
                    label={t("demandCharge.labels.startDate")}
                    option="stacked"
                  >
                    <MonthPickerInput
                      dropdownType="popover"
                      clearable
                      onChange={handleDateChange}
                      placeholder="Select month"
                      valueFormat="D-MMMM-YYYY"
                      required
                      disabled={isReadOnly || isFirstSeason()}
                      readOnly={isReadOnly || isFirstSeason()}
                      value={(() => {
                        const dateStr = selectedSeason?.startDay || "";
                        // Handle ISO-like strings robustly using moment, then construct local Date
                        if (
                          /^\d{4}-\d{2}-\d{2}T/.test(dateStr) ||
                          /^\d{4}-\d{2}-\d{2}$/.test(dateStr)
                        ) {
                          const m = moment(dateStr);
                          if (!m.isValid()) return null;
                          const y = m.year();
                          const mIdx = m.month(); // 0-based
                          const dNum = m.date();
                          return new Date(y, mIdx, dNum);
                        }
                        // dd-mm-yyyy format: parse explicitly and construct local Date
                        const [day, month, year] = dateStr.split("-");
                        if (day && month && year) {
                          const dNum = parseInt(day, 10);
                          const mIdx = parseInt(month, 10) - 1; // 0-based
                          const yNum = parseInt(year, 10);
                          if (
                            Number.isFinite(dNum) &&
                            Number.isFinite(mIdx) &&
                            Number.isFinite(yNum) &&
                            mIdx >= 0 && mIdx <= 11 &&
                            dNum >= 1 && dNum <= 31
                          ) {
                            return new Date(yNum, mIdx, dNum);
                          }
                          return null;
                        }
                        return null;
                      })()}
                      rightSection={
                        <div style={grayIconStyle}>
                          <IconCalendar size="1.1rem" stroke={1.5} />
                        </div>
                      }
                      rightSectionProps={{ style: { pointerEvents: "none" } }}
                      {...(() => {
                        // First season logic:
                        // - If there are other seasons (length > 1), Season 1 is locked
                        // - If Season 1 is alone, allow selection of past dates
                        //   BUT constrain maxDate so that (startDate + 1 year) <= today
                        //   This ensures all seasons fit within past/present, not extending to future
                        if (isFirstSeason()) {
                          const today = new Date();
                          // Only allow editing Season 1 date if it's the only season
                          if (seasonsList.length === 1) {
                            // maxDate = today - 1 year, so (selected date + 1 year) = today
                            const maxSelectDate = moment(today).subtract(1, 'years').startOf('month').toDate();
                            return {
                              minDate: new Date(1900, 0, 1), // Allow any date from past
                              maxDate: maxSelectDate // Ensures 1-year season stays within present
                            };
                          }
                          // If other seasons exist, Season 1 is locked (no date changes allowed)
                          return {};
                        }
                        // Non-first seasons: use existing dateConstraints
                        const { minDate, maxDate } = dateConstraints;
                        const constraints = {};
                        if (minDate) constraints.minDate = minDate;
                        if (maxDate) constraints.maxDate = maxDate;
                        return constraints;
                      })()}
                      popoverProps={{
                        withinPortal: true,
                        position: "bottom-start",
                        // disabled: isReadOnly || isFirstSeason(),
                        // styles: {
                        //   dropdown: { height: "400px" }
                        // }
                      }}
                    />
                    </se-form-field>
                </div>
                {!isReadOnly && (seasonsList.length > 1 || (props.getImportTariffSeasons?.() || props.importTariffSeasons)?.length > 0) && (
                  <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6 d-flex align-items-end pb-1" style={{ gap: 12, flexWrap: "wrap" }}>
                    <Checkbox
                      checked={copyFromEnabled}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        setCopyFromEnabled(checked);
                        if (!checked) setCopyFromSeasonId(null);
                      }}
                      label={<span style={{ fontWeight: 600, fontSize: 13 }}>{t('tariff.copyFrom') || 'Copy From'}</span>}
                      size="md"
                      color="green"
                    />
                    {copyFromEnabled && (
                        <Select
                          placeholder={t('tariff.copySeasonSelect') || 'Select season to copy from'}
                          data={[
                            ...seasonsList
                              .filter((s) => s.seasonId !== selectedSeason?.seasonId)
                              .map((s) => ({ value: `dc-${s.seasonId}`, label: s.name, group: t('tariff.copyFromGroup.demandCharge') || 'Demand Charge Seasons' })),
                            ...(props.getImportTariffSeasons?.() || props.importTariffSeasons || [])
                              .map((s) => ({ value: `it-${s.seasonId}`, label: s.name, group: t('tariff.copyFromGroup.importTariff') || 'Import Tariff Seasons' })),
                          ]}
                          value={copyFromSeasonId}
                          onChange={(val) => {
                            if (val) {
                              setPendingCopySeasonId(val);
                              setShowCopyConfirmModal(true);
                            }
                          }}
                          style={{ minWidth: 200 }}
                          withinPortal
                          classNames={{ separatorLabel: "copy-from-select-group-label" }}
                        />
                    )}
                  </div>
                )}
                {/* <div className="col-sm-12 col-md-12 col-lg-6 col-xl-6 ">
                    <se-form-field
                    // padding="none"
                    // label-align="left"
                    // label-width="30%"
                    option="stacked"
                    type="select"
                    label={t("demandCharge.labels.startTime")}
                    required={true}
                  >
                    <Select
                      data={timeOptions}
                      value={
                        formatTime(
                          selectedSeason?.seasonStartTime?.hour,
                          selectedSeason?.seasonStartTime?.minute
                        ) || null
                      }
                      onChange={handleStartTimeChange}
                    />
                    </se-form-field>
                </div> */}
              </div>
              <div className="row">
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3 mt-3">
                  {" "}
                  {/* <span className="header-content-roman">II. </span> */}
                  <span className="header-content">
                    {t("demandCharge.labels.defineDemandType")}
                  </span>
                </div>
               
                <div className="col-sm-12 col-md-12 col-xl-12 ps-3">
                  <DynamicTabbar
                    items={selectedSeason?.periods || []}
                    activeIndex={activeDemandType}
                    editingIndex={editingDemandTypeIndex}
                    newItemValue={newDemandTypeValue}
                    handleItemClick={handleDemandTypeClick}
                    handleDoubleClick={handleDemandTypeDoubleClick}
                    handleInputChange={handleDemandTypeInputChange}
                    handleInputBlur={handleDemandTypeInputBlur}
                    removeItem={removeDemandType}
                    addItem={addDemandType}
                    itemKey="demandTypeId"
                    itemName="demandTypeName"
                    itemId="demandTypeId"
                    showAddRemove={!props.isVersionCompleted}
                  />
                  <WeekdaysSelector
                    WEEK_DAYS={WEEK_DAYS}
                    usedWeekdays={usedWeekdays}
                    selectedSeason={selectedSeason}
                    activeDemandType={activeDemandType}
                    handleWeekdayClick={handleWeekdayClick}
                  />
                 
                </div>
                <div className="col-sm-12 col-md-12 col-xl-12">
                  <TimeSlotsDisplay
                    selectedSeason={selectedSeason}
                    activeDemandType={activeDemandType}
                  />
                </div>
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3 mt-3">
                  {" "}
                  <span style={{fontSize: "14px", fontWeight: "500"}}>
                    {t("demandCharge.labels.chooseDemandParametersDesc")}
                  </span>
                </div>
                 <div className="col-sm-12 col-md-12 col-xl-12">
                  <DemandNoticeSelector
                    DEMAND_NOTICE={DEMAND_NOTICE}
                    selectedOptions={selectedDemandNoticeOptions}
                    handleOptionClick={handleDemandNoticeOptionClick}
                    removeOptionFromAllTiers={removeOptionFromAllTiers}
                    tierCount={tierCount}
                    isMasterContext={isMasterContext}
                    isReadOnly={isReadOnly}
                    onTariffChange={props.onChange}
                  />
                </div>
                  <div
                    style={{ margin: "16px 0", display: "flex", alignItems: "center", gap: 12 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 40 }}>
                      <Checkbox
                        checked={!!isNcCost}
                        disabled={isReadOnly}
                        onChange={() => {
                          if (isReadOnly) return;
                          props?.onChange?.();
                          const next = !isNcCost;
                          setIsNcCost(next);
                          setSeasonsList((prev) => prev.map((season, idx) =>
                            idx === activeSeason ? { ...season, isNcCost: next } : season
                          ));
                          setSelectedSeason((prev) => ({ ...prev, isNcCost: next }));
                        }}
                        label={
                          <span className="font-11px" style={{ textTransform: 'uppercase', fontWeight: 700, marginBottom: 0 }}>
                            {t('demandCharge.labels.nonCoincidentalCost')}
                          </span>
                        }
                        styles={{
                          label: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '12px' }
                        }}
                        size="lg"
                        color="green"
                      />
                    </div>
                    <div
                      style={{
                        width: 130,
                        transition: "opacity 0.2s",
                        opacity: isNcCost ? 1 : 0.5,
                        pointerEvents: isNcCost ? "auto" : "none",
                        display: isNcCost? "block" : "none",
                      }}
                    >
                      <TextInput
                        value={isNcCost ? ncCost : ""}
                        onChange={(e) => {
                          if (isReadOnly) return;
                          props?.onChange?.();
                          const raw = e.target.value;
                          if (raw === "" || DECIMAL_INPUT_REGEX.test(raw)) {
                            setNcCost(raw);
                            setSeasonsList((prev) => prev.map((season, idx) =>
                              idx === activeSeason ? { ...season, ncCost: raw } : season
                            ));
                            setSelectedSeason((prev) => ({ ...prev, ncCost: raw }));
                          }
                        }}
                        size="md"
                        placeholder={t("demandCharge.labels.enterNcCost")}
                        inputMode="decimal"
                        styles={{ input: { paddingLeft: 10, paddingRight: 10 } }}
                        disabled={isReadOnly || !isNcCost}
                      />
                    </div>
                  </div>
              </div>
              <div className="row">
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3">
                  {" "}
                  {/* <span className="header-content-roman">III. </span> */}
                  <span className="header-content">
                    {t("demandCharge.labels.defineTariffPeriod")}
                  </span>
                </div>
              </div>
              {!isReadOnly && <div className="row ps-3">
                <div className="col-sm-12 col-md-12 col-xl-12 d-flex mb-3">
                  <Group>
                    <Button
                      aria-label="demand-charge-add-period"
                      onClick={addPeriodToDemandType}
                      style={{ backgroundColor: isMasterContext && !isReadOnly ? "#42b4e6" : "#ccc" }}
                      disabled={!isMasterContext || isReadOnly}
                    >
                      <SeIcon>electricity_polarity_positive_plus</SeIcon>
                      {t("demandCharge.buttons.addPeriod")}
                    </Button>
                    <Button 
                      aria-label="demand-charge-remove-period"
                      color={"gray"} 
                      onClick={removePeriodFromDemandType} 
                      disabled={!isMasterContext || isReadOnly}
                    >
                      <SeIcon>electricity_polarity_negative_minus</SeIcon>
                      {t("demandCharge.buttons.removePeriod")}
                    </Button>
                    <Button
                      aria-label="demand-charge-add-tier"
                      onClick={addTierToDemandType}
                      style={{ backgroundColor: isMasterContext && !isReadOnly ? "#42b4e6" : "#ccc" }}
                      disabled={!isMasterContext || isReadOnly}
                    >
                      <SeIcon>electricity_polarity_positive_plus</SeIcon>
                      {t("demandCharge.buttons.addTier")}
                    </Button>
                    <Button aria-label="demand-charge-remove-tier" color={"gray"} onClick={removeTierfromDemandType} disabled={!isMasterContext || isReadOnly}>
                      <SeIcon>electricity_polarity_negative_minus</SeIcon>
                      {t("demandCharge.buttons.removeTier")}
                    </Button>
                  </Group>
                </div>
              </div>}
            </div>
          </div>
          <div className="row ps-3">
            {selectedSeason?.periods[activeDemandType] && (
              <TariffDataSheet
                activeDemandType={selectedSeason?.periods[activeDemandType]}
                seasonsList={seasonsList}
                selectedSeason={selectedSeason}
                activeDemandTypeIndex={activeDemandType}
                updateTier={updateTierInDayGroup}
                updatePeriodName={updatePeriodName}
                updateTimeSlot={updateTimeSlot}
                callSnackbar={callSnackbar}
                handleRemoveTimeSlots={handleRemoveTimeSlots}
                currentDate={currentDate}
                updateTierFractionInDayGroup={updateTierFractionInDayGroup}
                updateSelectedSeasonAndDayGroup={
                  updateSelectedSeasonAndDayGroup
                }
                allowBill={props.allowBill}
                allowPenalty={props.allowPenalty}
                batchUpdateTierFractionsInDayGroup={batchUpdateTierFractionsInDayGroup}
                batchUpdateRachetGlobally={batchUpdateRachetGlobally}
                updatePeriodColor={updatePeriodColor}
                isMasterContext={isMasterContext}
                isReadOnly={isReadOnly}
              />
            )}
          </div>
        </div>
      </div>

      {/* Confirmation Modal for Removing Season */}
      <SEConfirmModal
        opened={showConfirmRemoveSeason}
        onClose={() => {
          setShowConfirmRemoveSeason(false);
          setSeasonToRemove(null);
        }}
        onConfirm={confirmRemoveSeason}
        header={t("demandCharge.modal.confirmRemoveSeason", { defaultValue: "Alert" })}
        message={t("demandCharge.modal.removeSeasonMessage", { defaultValue: "Are you sure you want to delete? This action cannot be undone." })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />

      {/* Confirmation Modal for Removing Demand Type */}
      <SEConfirmModal
        opened={showConfirmRemoveDemandType}
        onClose={() => {
          setShowConfirmRemoveDemandType(false);
          setDemandTypeToRemove(null);
        }}
        onConfirm={confirmRemoveDemandType}
        header={t("demandCharge.modal.confirmRemoveDemandType", { defaultValue: "Alert" })}
        message={t("demandCharge.modal.removeDemandTypeMessage", { defaultValue: "Are you sure you want to delete? This action cannot be undone." })}
        cancelLabel={t("common.cancel", { defaultValue: "Cancel" })}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />

    </>
  );
});

DCTariff.propTypes = {
  tariffTemplate: PropTypes.array,
  importTariffSeasons: PropTypes.array,
  getImportTariffSeasons: PropTypes.func,
};